import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ActionSidePanel from 'src/components/design-system/ActionSidePanel';
import Button from 'src/components/design-system/Button';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import useWindowSize from 'src/hooks/useWindowSize';
import { ManageSupplier } from 'src/types/manage-suppliers/ManageSupplier';
import { SupplierFormValues } from 'src/types/manage-suppliers/SupplierForm';
import SupplierForm from './SupplierForm';
import { useEditSupplierForm } from './useEditSupplierForm';
import { getSupplierSidePanelWidth } from './utils/supplierSidePanelWidth';

export interface EditSupplierSidePanelProps {
  isOpen: boolean;
  supplier?: ManageSupplier | null;
  onClose: () => void;
  onSaveSuccess?: () => void | Promise<void>;
  /** View Catalogs: start read-only, enable fields on Edit click. */
  viewUntilEdit?: boolean;
  canEnterEditMode?: boolean;
  saveOnlineOverride?: (
    values: SupplierFormValues,
    supplierClientId: number
  ) => Promise<void>;
  showProgramInfo?: boolean;
  hasProgramInfo?: boolean;
  onProgramInfoClick?: () => void;
  isLoading?: boolean;
}

const EditSupplierSidePanel: FC<EditSupplierSidePanelProps> = ({
  isOpen,
  supplier = null,
  onClose,
  onSaveSuccess,
  viewUntilEdit = false,
  canEnterEditMode = true,
  saveOnlineOverride,
  showProgramInfo = false,
  hasProgramInfo = false,
  onProgramInfoClick,
  isLoading=false,
}) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { width: viewportWidth } = useWindowSize();
  const panelWidth = getSupplierSidePanelWidth(viewportWidth);

  const {
    values,
    errors,
    touched,
    isDirty,
    isValid,
    isSaving,
    isEditing,
    editAccess,
    fieldAccess,
    requiredFields,
    canShowEditButton,
    ownershipBanner,
    showUnlinkedIndicator,
    emailValidationStatus,
    emailValidationDateTime,
    emailValidationExpired,
    showResendValidation,
    isResendingValidation,
    enrichedSupplier,
    setFieldValue,
    handleFieldBlur,
    handleSave,
    handleEditClick,
    exitEditMode,
    handleResendValidationRequest,
  } = useEditSupplierForm({
    supplier,
    isOpen,
    onSuccess: onSaveSuccess ?? (viewUntilEdit ? undefined : onClose),
    viewUntilEdit,
    canEnterEditMode,
    saveOnlineOverride,
  });


  const showTransmissionSettings = enrichedSupplier?.supplierType === 'offline';

  const panelTitle = useMemo(() => {
    if (viewUntilEdit && !isEditing) {
      return t('Supplier Info');
    }

    if (editAccess?.mode === 'view') {
      return t('Supplier Info');
    }

    const editLabel =
      enrichedSupplier?.supplierType !== 'offline'
        ? t('Edit Online Supplier')
        : t('Edit Offline Supplier');
    const supplierName = values?.supplierName.trim();

    return supplierName ? `${editLabel} - ${supplierName}` : editLabel;
  }, [
    editAccess?.mode,
    enrichedSupplier?.supplierType,
    isEditing,
    t,
    values?.supplierName,
    viewUntilEdit,
  ]);

  const confirmDiscardChanges = async (): Promise<boolean> => {
    if (!isDirty) {
      return true;
    }

    return confirm({
      title: t('Unsaved Changes'),
      content: t('You have unsaved changes. Do you want to discard them?'),
      cancelButtonText: t('Cancel'),
      confirmButtonText: t('Yes, Discard'),
      width: '500px',
    });
  };

  const handleCancelEdit = async () => {
    const shouldCancel = await confirmDiscardChanges();
    if (shouldCancel) {
      exitEditMode();
    }
  };

  const handleClose = async () => {
    const shouldClose = await confirmDiscardChanges();
    if (shouldClose) {
      onClose();
    }
  };

  const isInEditMode = viewUntilEdit ? isEditing : editAccess?.canSave;
  const showSave = Boolean(isInEditMode && editAccess?.canSave);
  const showCancel = Boolean(isInEditMode && editAccess?.canSave);
  const saveDisabledTooltip = !isDirty ? (
    <PopUpContentWrapper type="Information" contents={[t('No changes made')]} />
  ) : undefined;
  const hasActionBar = Boolean(canShowEditButton) || showSave || showCancel;

  if (!values || !editAccess) {
    return null;
  }

  return (
    <ActionSidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={panelTitle}
      onSave={handleSave}
      onCancel={viewUntilEdit ? handleCancelEdit : handleClose}
      saveDisabled={!editAccess.canSave || !isValid || !isDirty}
      saveLoading={isSaving || isLoading}
      cancelDisabled={isSaving}
      saveDisabledTooltip={saveDisabledTooltip}
      showSave={showSave}
      showCancel={showCancel}
      width={panelWidth}
      headerActions={
        canShowEditButton ? (
          <Button type="button" color="info" size="md" onClick={handleEditClick}>
            {t('Edit')}
          </Button>
        ) : undefined
      }
    >
      <SupplierForm
        mode="edit"
        values={values}
        errors={errors}
        touched={touched}
        fieldAccess={fieldAccess}
        requiredFields={requiredFields}
        onFieldChange={setFieldValue}
        onFieldBlur={handleFieldBlur}
        ownershipBanner={ownershipBanner}
        hasActionBar={hasActionBar}
        showUnlinkedIndicator={showUnlinkedIndicator}
        emailValidationStatus={emailValidationStatus}
        emailValidationDateTime={emailValidationDateTime}
        emailValidationExpired={emailValidationExpired}
        onResendValidationRequest={handleResendValidationRequest}
        isResendingValidation={isResendingValidation}
        showResendValidation={showResendValidation}
        showProgramInfo={showProgramInfo}
        hasProgramInfo={hasProgramInfo}
        onProgramInfoClick={onProgramInfoClick}
        showTransmissionSettings={showTransmissionSettings}
      />
    </ActionSidePanel>
  );
};

export default EditSupplierSidePanel;
